import React, { useState, useEffect } from 'react';

function LiveNotifications({ activities, currentUser, viewMode = 'collaborative' }) {
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [visibleNotifications, setVisibleNotifications] = useState([]);

  useEffect(() => {
    if (!activities || activities.length === 0 || viewMode !== 'collaborative') return;

    // Filter out current user's activities and get only recent ones (last 5 minutes)
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    const otherUsersActivities = activities.filter(activity => {
      const activityTime = new Date(activity.created_at);
      return activity.user_id !== currentUser?.id && 
             activityTime > fiveMinutesAgo;
    });

    // Check for new activities
    const existingIds = new Set(recentNotifications.map(n => n.id));
    const newActivities = otherUsersActivities.filter(activity => !existingIds.has(activity.id));

    if (newActivities.length > 0) {
      setRecentNotifications(prev => {
        const updated = [...newActivities, ...prev].slice(0, 10); // Keep only last 10
        return updated;
      });

      // Show notifications for new activities
      newActivities.forEach(activity => {
        showNotification(activity);
      });
    }
  }, [activities, currentUser, recentNotifications, viewMode]);

  const showNotification = (activity) => {
    const notification = {
      id: activity.id,
      activity,
      timestamp: Date.now()
    };

    setVisibleNotifications(prev => [...prev, notification]);

    // Auto-remove after 4 seconds
    setTimeout(() => {
      setVisibleNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 4000);
  };

  const getNotificationMessage = (activity) => {
    const userName = activity.user_name || activity.user_email?.split('@')[0] || 'Someone';
    
    switch (activity.action_type) {
      case 'moved_ticket':
        return `${userName} moved ${activity.ticket_key} from ${activity.from_board} to ${activity.to_board}`;
      case 'created_ticket':
        return `${userName} created ticket ${activity.ticket_key}`;
      case 'updated_ticket':
        return `${userName} updated ${activity.ticket_key}`;
      case 'deleted_ticket':
        return `${userName} deleted ticket ${activity.ticket_key}`;
      case 'created_board':
        return `${userName} created board "${activity.details?.boardName}"`;
      case 'updated_board':
        return `${userName} updated board "${activity.details?.boardName}"`;
      case 'deleted_board':
        return `${userName} deleted board "${activity.details?.boardName}"`;
      case 'reordered_boards':
        return `${userName} reordered boards`;
      case 'reordered_tickets':
        return `${userName} reordered tickets in ${activity.details?.boardName}`;
      case 'updated_board_status':
        return `${userName} changed "${activity.details?.boardName}" status to ${activity.details?.newStatus}`;
      case 'imported_tickets':
        return `${userName} imported ${activity.details?.count} tickets`;
      default:
        return `${userName} performed ${activity.action_type}`;
    }
  };

  const getNotificationIcon = (actionType) => {
    const icons = {
      'moved_ticket': '🔄',
      'created_ticket': '➕',
      'updated_ticket': '✏️',
      'deleted_ticket': '🗑️',
      'created_board': '📋',
      'updated_board': '📝',
      'deleted_board': '❌',
      'reordered_boards': '↕️',
      'reordered_tickets': '🔃',
      'updated_board_status': '🔄',
      'imported_tickets': '📥'
    };
    return icons[actionType] || '📢';
  };

  const getUserColor = (email) => {
    if (!email) return '#007bff';
    const colors = ['#007bff', '#28a745', '#dc3545', '#ffc107', '#17a2b8', '#6f42c1', '#fd7e14'];
    const hash = email.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return colors[Math.abs(hash) % colors.length];
  };

  if (viewMode !== 'collaborative' || visibleNotifications.length === 0) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      top: '80px',
      right: '20px',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      maxWidth: '350px'
    }}>
      {visibleNotifications.map(({ id, activity }) => (
        <div
          key={id}
          style={{
            background: 'white',
            borderRadius: '8px',
            padding: '12px 16px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            border: `2px solid ${getUserColor(activity.user_email)}`,
            animation: 'slideInRight 0.3s ease-out',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '14px',
            maxWidth: '100%',
            wordWrap: 'break-word'
          }}
        >
          <div style={{
            fontSize: '18px',
            flexShrink: 0
          }}>
            {getNotificationIcon(activity.action_type)}
          </div>
          
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontWeight: '500',
              color: '#333',
              lineHeight: '1.3',
              wordBreak: 'break-word'
            }}>
              {getNotificationMessage(activity)}
            </div>
            <div style={{
              fontSize: '11px',
              color: '#666',
              marginTop: '2px'
            }}>
              just now
            </div>
          </div>

          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: getUserColor(activity.user_email),
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            fontWeight: 'bold',
            flexShrink: 0
          }}>
            {(activity.user_name || activity.user_email || 'U')[0].toUpperCase()}
          </div>
        </div>
      ))}

      <style jsx>{`
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
}

export default LiveNotifications;